import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";

import { useCancelSubscription } from "$/api/payment";
import { Button } from "$/lib/components";
import LoadingData from "$/lib/components/molecules/LoadingData/LoadingData";
import Tag from "$/lib/components/molecules/Tag/Tag";
import CalendarIcon from "$/lib/icons/CalendarIcon";
import CloseIcon from "$/lib/icons/CloseIcon";
import CreditcardIcon from "$/lib/icons/CreditcardIcon";
import DisconnectedWarningIcon from "$/lib/icons/DisconnectedWarningIcon";
import LibeIcon from "$/lib/icons/LibeIcon";
import LoadingCircularIcon from "$/lib/icons/LoadingCircularIcon";
import { useAuth } from "$/lib/providers/AuthProvider";
import {
  PlanIntervalEnum,
  SubscriptionStatusEnum,
} from "$/lib/types/models/Subscription.types";
import { Analytics } from "$/lib/utils/analytics";
import { SubscriptionStatusMapper } from "$/lib/utils/constants/payment.constants";
import { formatNumberToFrench } from "$/lib/utils/functions/misc.functions";

import InvoicesList from "./-features/InvoicesList";
import PaymentMethodsList from "./-features/PaymentMethodsList";
import PaymentPlansList from "./-features/PaymentPlansList";

export const Route = createFileRoute("/_user-layout/Abonnement/paiement")({
  component: SubscriptionPage,
});

// Types
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

// Mock components
const Modal = ({ isOpen, onClose, children, className = "" }: ModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-primary/10 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl ${className}`}
      >
        {children}
      </div>
    </div>
  );
};

// Composant principal
export default function SubscriptionPage() {
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { user, isFetching } = useAuth();

  const { mutate, isPending } = useCancelSubscription();

  const currentUserPlan = user?.subscription?.plan;
  const hasSubscription =
    Boolean(currentUserPlan) &&
    user?.subscription?.status !== SubscriptionStatusEnum.CANCELED;
  const currentUserPlanStatus = user?.subscription?.status
    ? SubscriptionStatusMapper[user?.subscription?.status]
    : { label: "N/A", color: "#EFEFEF", backgroundColor: "#F5F5F5" };
  const isCancelling = (
    [
      SubscriptionStatusEnum.CANCELED,
      SubscriptionStatusEnum.CANCELLING,
    ] as string[]
  ).includes(String(user?.subscription?.status));

  const [renewalTitle, renewalLabel, renewalDate] = useMemo(() => {
    const isInTrialPeriod =
      String(user?.subscription?.status) === SubscriptionStatusEnum.TRIALING ||
      (user?.freeTrialEndsAt != null &&
        new Date(user.freeTrialEndsAt) > new Date());

    // Determine the section title
    const title = isCancelling
      ? isInTrialPeriod
        ? "Résiliation en cours, vous ne serez pas prélevé"
        : "Résiliation en cours"
      : hasSubscription
        ? isInTrialPeriod
          ? "Abonné, prélèvement à l'issue de l'essai"
          : user?.subscription.plan.interval === PlanIntervalEnum.MONTH
            ? "Formule mensuelle"
            : "Formule annuelle"
        : "Pas d'abonnement";

    // Determine the label
    let label: string;
    if (isInTrialPeriod) {
      label = "Fin de la période d'essai";
    } else {
      if (!hasSubscription) {
        label = "-";
      } else if (isCancelling) {
        label = "Abonné jusqu'au";
      } else {
        label = "Renouvellement";
      }
    }

    // Determine the date
    let date: string;
    if (isInTrialPeriod && user?.freeTrialEndsAt) {
      date = format(new Date(user.freeTrialEndsAt), "dd/MM/yyyy");
    } else if (
      user?.subscription?.stripeCurrentPeriodEnd &&
      user.subscription.status !== SubscriptionStatusEnum.CANCELED
    ) {
      date = format(
        new Date(user.subscription.stripeCurrentPeriodEnd),
        "dd/MM/yyyy",
      );
    } else if (user?.freeTrialEndsAt) {
      date = format(new Date(user.freeTrialEndsAt), "dd/MM/yyyy");
    } else {
      date = "-";
    }

    return [title, label, date];
  }, [
    user?.subscription?.status,
    user?.subscription?.stripeCurrentPeriodEnd,
    user?.freeTrialEndsAt,
    isCancelling,
    hasSubscription,
    currentUserPlan?.interval,
  ]);

  const handleCancelSubscription = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = () => {
    mutate();
    setShowCancelConfirm(false);
  };

  if (isFetching)
    return (
      <div className="relative flex min-h-screen flex-col items-center justify-center gap-8">
        <LoadingData />
      </div>
    );

  return (
    <div className="mt-6 w-[93%] mx-auto screen850:w-full screen850:mt-8">
      <p className="mb-6 screen850:mb-8 text-[15px] screen850:text-base font-bold text-textColor">
        Gérer mon abonnement
      </p>
      <div className="flex flex-col items-stretch gap-6 screen850:flex-row">
        {/* Left Section - Ma formule actuelle (40%) */}
        <div className="flex w-full flex-col justify-between gap-6 rounded-xl border border-border bg-white p-5 shadow-sm screen850:w-[45%]">
          {/* Header avec icône et prix */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 screen850:h-12 screen850:w-12 items-center justify-center rounded-lg border border-border">
                <LibeIcon className="h-5 w-5 text-textColor" />
              </div>
              <div className="flex flex-row justify-between gap-2">
                <div className="flex flex-row items-center gap-2">
                  <h2 className="text-[15px] screen850:text-base font-bold text-textColor">
                    Ma formule
                  </h2>
                  <span className="hidden text-xs text-gray-500">
                    ({currentUserPlan?.name})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {user?.subscription?.status && (
                    <Tag
                      label={currentUserPlanStatus.label}
                      textColor={currentUserPlanStatus.color}
                      backgroundColor={currentUserPlanStatus.backgroundColor}
                      className="h-fit px-3 py-1 text-[10px]"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center">
              <div className="text-[24px] font-bold text-textColor">
                {currentUserPlan
                  ? formatNumberToFrench(
                      Number(currentUserPlan?.unitAmount) / 100,
                    ) || "-"
                  : "- €"}{" "}
              </div>
              {currentUserPlan && (
                <div className="text-xs font-semibold text-[#696680]">
                  par{" "}
                  {currentUserPlan?.interval === PlanIntervalEnum.MONTH
                    ? "mois"
                    : "an"}
                </div>
              )}
            </div>
          </div>

          {/* Moyen de paiement */}
          <PaymentMethodsList />

          {/* Renouvellement */}
          <div className="">
            <h3 className="mb-1 text-sm font-semibold text-[#1E1C2D]">
              {renewalTitle}
            </h3>
            {hasSubscription ? (
              <div className="flex items-center justify-between rounded-xl border border-border min-h-[50px] px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-info">
                    <CalendarIcon className="h-4 w-4 text-textInfo" />
                  </div>
                  <span className="text-sm font-semibold text-textColor">
                    {renewalLabel}
                  </span>
                </div>
                <span className="text-sm font-semibold text-[#1E1C2D]">
                  {renewalDate}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 min-h-[50px] text-sm font-semibold text-textInfo">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-info">
                  <CalendarIcon className="h-4 w-4 text-textInfo" />
                </div>
                Aucune formule sélectionnée.
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mb-1 flex w-full justify-center">
            <div className="w-fit">
              {hasSubscription && (
                <Button
                  theme="ghost"
                  className="flex-1 bg-info px-3 py-2 text-sm font-semibold text-textInfo"
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                >
                  Suspendre l'abonnement
                </Button>
              )}
              {!hasSubscription && (
                <Button
                  theme="primary"
                  className="flex-1 px-3 py-2 text-sm font-medium"
                  onClick={() => {
                    setShowPlansModal(true);
                    Analytics.cta.click({
                      page: "app_payment",
                      event_category: "conversion",
                      cta_text: "choisir_formule",
                    });
                  }}
                >
                  <span className="font-medium">Choisir une formule</span>
                </Button>
              )}
            </div>
          </div>
        </div>
        {/* Right Section - Historique de facturation (60%) */}
        <InvoicesList className="w-full screen850:w-[55%]" />
      </div>

      {/* Modal de confirmation de suspension */}
      <Modal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        className="mx-4 flex w-full max-w-[450px] flex-col items-center justify-center p-6 screen850:mx-0"
      >
        <div className="relative mb-4 flex items-start justify-between">
          <div className="flex w-full flex-col items-center justify-center">
            <DisconnectedWarningIcon className="w-15 h-15 mb-4" />
            <h2 className="w-full pb-2 text-center text-base font-bold">
              Vous résiliez votre abonnement
            </h2>
            <p className="w-[95%] w-full pt-2 text-justify text-sm font-semibold text-textColor">
              Le renouvellement est suspendu. Vous pouvez continuer à profiter
              de Libe jusqu’à cette écheance.
            </p>
          </div>
          <button
            onClick={() => setShowCancelConfirm(false)}
            className="absolute right-3 top-3 hidden h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#F5F5F7]"
          >
            <CloseIcon className="h-4 w-4 text-textInfo" />
          </button>
        </div>
        <div className="mt-4 flex w-[83%] justify-center gap-3">
          <Button
            disabled={isPending}
            theme="ghost"
            className="w-full bg-info px-4 py-2 text-sm font-semibold text-textInfo"
            onClick={() => setShowCancelConfirm(false)}
          >
            Annuler
          </Button>
          <Button
            disabled={isPending}
            theme="primary"
            className="w-full whitespace-nowrap bg-danger px-4 py-2 text-sm font-medium"
            onClick={handleConfirmCancel}
          >
            Confirmer
          </Button>
        </div>
      </Modal>

      {/* Modal pour changer de formule */}
      <Modal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        className="mx-4 w-full p-6 screen850:mx-0 screen850:w-fit"
      >
        {/* Header Modal */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex flex-col">
            <h2 className="mb-2 text-xl font-bold text-textColor">
              Choisissez votre formule
            </h2>
            <div className="flex w-fit flex-row items-center justify-start gap-2 rounded-full bg-secondary-50 px-3 py-1">
              <span className="text-base font-semibold text-secondary-300">
                Offre de lancement
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowPlansModal(false)}
            className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#F5F5F7]"
          >
            <CloseIcon className="h-4 w-4 text-textInfo" />
          </button>
        </div>

        {/* Plans Cards */}
        <PaymentPlansList />
      </Modal>
    </div>
  );
}
